import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/Layout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PhoneCall, UserPlus, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const BLANK = {
  name: "", phone: "", email: "", company: "",
  address: "", city: "", state: "", zip: "",
  lead_source: "", service_interest: "", notes: "",
};
const SOURCES = ["Google", "Facebook", "Referral", "Repeat Customer", "Yard Sign", "Nextdoor", "Other"];

export default function Intake() {
  const [f, setF] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const nameRef = useRef();
  const navigate = useNavigate();

  useEffect(() => { nameRef.current?.focus(); }, []);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const build = () => {
    if (!f.name.trim()) { toast.error("At least capture a name"); return null; }
    const properties = f.address.trim()
      ? [{ address: f.address, city: f.city, state: f.state, zip: f.zip, notes: "" }]
      : [];
    return {
      name: f.name, phone: f.phone, email: f.email, company: f.company,
      lead_source: f.lead_source, service_interest: f.service_interest,
      notes: f.notes, properties,
    };
  };

  const save = async ({ then }) => {
    const payload = build();
    if (!payload) return;
    setSaving(true);
    try {
      const c = await api.createClient(payload);
      toast.success(`${c.name} saved`);
      if (then === "another") { setF(BLANK); nameRef.current?.focus(); }
      else navigate(`/clients/${c.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-8 max-w-[900px]">
      <PageHeader title="New Customer Intake" subtitle="Quickly capture details while you're on the call" />

      <div className="bg-[#0B1726] rounded-lg px-5 py-3 mb-6 flex items-center gap-3">
        <PhoneCall size={20} weight="duotone" className="text-[#00B4D8]" />
        <span className="text-slate-200 text-sm">On a call? Just fill what you have — only a name is required. Hit <span className="font-semibold text-white">Save &amp; Add Another</span> to keep going.</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm space-y-6">
        {/* Contact */}
        <section>
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Contact</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Name *</Label><Input ref={nameRef} value={f.name} onChange={set("name")} placeholder="Customer name" className="mt-1.5" data-testid="intake-name" /></div>
            <div><Label>Phone</Label><Input value={f.phone} onChange={set("phone")} placeholder="(555) 123-4567" className="mt-1.5" data-testid="intake-phone" /></div>
            <div><Label>Email</Label><Input type="email" value={f.email} onChange={set("email")} placeholder="name@email.com" className="mt-1.5" data-testid="intake-email" /></div>
            <div><Label>Company (optional)</Label><Input value={f.company} onChange={set("company")} className="mt-1.5" /></div>
          </div>
        </section>

        {/* Property */}
        <section>
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Service Address</h3>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-3"><Label>Street</Label><Input value={f.address} onChange={set("address")} placeholder="123 Main St" className="mt-1.5" data-testid="intake-address" /></div>
            <div className="md:col-span-1"><Label>City</Label><Input value={f.city} onChange={set("city")} className="mt-1.5" /></div>
            <div className="md:col-span-1"><Label>State</Label><Input value={f.state} onChange={set("state")} className="mt-1.5" /></div>
            <div className="md:col-span-1"><Label>Zip</Label><Input value={f.zip} onChange={set("zip")} className="mt-1.5" /></div>
          </div>
        </section>

        {/* Lead details */}
        <section>
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Lead Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>How did they hear about us?</Label>
              <Select value={f.lead_source} onValueChange={(v) => setF((p) => ({ ...p, lead_source: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="intake-source"><SelectValue placeholder="Select source" /></SelectTrigger>
                <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Service interested in</Label><Input value={f.service_interest} onChange={set("service_interest")} placeholder="e.g. House wash + driveway" className="mt-1.5" data-testid="intake-service" /></div>
          </div>
          <div className="mt-4"><Label>Notes</Label><Textarea value={f.notes} onChange={set("notes")} placeholder="Anything else they mentioned — gate codes, pets, best time to call back…" className="mt-1.5 min-h-[100px]" data-testid="intake-notes" /></div>
        </section>

        <div className="flex flex-wrap gap-3 pt-2 border-t border-slate-100">
          <Button onClick={() => save({ then: "another" })} disabled={saving} className="bg-[#00B4D8] hover:bg-cyan-600" data-testid="intake-save-another">
            <UserPlus size={18} className="mr-1.5" /> {saving ? "Saving…" : "Save & Add Another"}
          </Button>
          <Button onClick={() => save({ then: "open" })} disabled={saving} variant="outline" data-testid="intake-save-open">
            Save & Open Profile <ArrowRight size={16} className="ml-1.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
